import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-markercluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";

// Custom Apple-style price pill icon for individual markers
const createMarkerIcon = (price) => {
  return L.divIcon({
    className: "custom-marker",
    html: `<div style="
      background: #000;
      color: #fff;
      font-family: 'Manrope', sans-serif;
      font-size: 13px;
      font-weight: 600;
      padding: 6px 12px;
      border-radius: 20px;
      white-space: nowrap;
      box-shadow: 0 4px 16px rgba(0,0,0,0.2);
      transform: translate(-50%, -100%);
      cursor: pointer;
    ">${price} €</div>`,
    iconSize: [0, 0],
    iconAnchor: [0, 0],
    popupAnchor: [0, -8],
  });
};

// Custom Apple-style cluster bubble (count badge)
const createClusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  const size = count < 10 ? 40 : count < 25 ? 48 : 56;
  return L.divIcon({
    html: `<div style="
      width: ${size}px;
      height: ${size}px;
      background: linear-gradient(135deg, #1D1D1F 0%, #000 100%);
      color: #fff;
      font-family: 'Outfit', sans-serif;
      font-size: ${count < 10 ? 14 : 13}px;
      font-weight: 600;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      box-shadow: 0 6px 24px rgba(0,0,0,0.25), 0 0 0 4px rgba(255,255,255,0.95);
      cursor: pointer;
      transition: transform 0.3s cubic-bezier(0.25, 1, 0.5, 1);
    ">${count}</div>`,
    className: "custom-cluster-icon",
    iconSize: L.point(size, size, true),
  });
};

// Module-level constants (avoid re-creation on every render)
const MAP_STYLE_DESKTOP = { height: "600px", width: "100%" };
const MAP_STYLE_MOBILE = { height: "420px", width: "100%" };
const TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';
const TILE_URL = "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";

export default function ListingsMap({ listings = [] }) {
  const navigate = useNavigate();

  const validListings = useMemo(
    () => listings.filter((l) => l.lat != null && l.lng != null),
    [listings]
  );

  const center = useMemo(() => {
    if (!validListings.length) return [48.2082, 16.3738];
    const avgLat = validListings.reduce((s, l) => s + l.lat, 0) / validListings.length;
    const avgLng = validListings.reduce((s, l) => s + l.lng, 0) / validListings.length;
    return [avgLat, avgLng];
  }, [validListings]);

  // Pick height based on viewport — narrow screens get a shorter map for better readability
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const mapStyle = isMobile ? MAP_STYLE_MOBILE : MAP_STYLE_DESKTOP;

  if (!validListings.length) {
    return (
      <div className="flex items-center justify-center h-[420px] md:h-[600px] bg-[#F5F5F7] rounded-3xl border border-black/5">
        <p className="text-[#86868B]">Keine Standorte verfügbar</p>
      </div>
    );
  }

  return (
    <div data-testid="listings-map" className="rounded-3xl overflow-hidden border border-black/5 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
      <MapContainer
        center={center}
        zoom={13}
        style={mapStyle}
        scrollWheelZoom={true}
        zoomControl={true}
      >
        <TileLayer attribution={TILE_ATTRIBUTION} url={TILE_URL} />
        <MarkerClusterGroup
          chunkedLoading
          showCoverageOnHover={false}
          spiderfyOnMaxZoom
          spiderfyDistanceMultiplier={2}
          maxClusterRadius={45}
          iconCreateFunction={createClusterIcon}
        >
          {validListings.map((listing) => (
            <ListingMarker key={listing.id} listing={listing} navigate={navigate} />
          ))}
        </MarkerClusterGroup>
      </MapContainer>
    </div>
  );
}

// Individual marker component — lets us memoize each marker's position array
function ListingMarker({ listing, navigate }) {
  const position = useMemo(() => [listing.lat, listing.lng], [listing.lat, listing.lng]);
  const icon = useMemo(() => createMarkerIcon(listing.price), [listing.price]);

  return (
    <Marker position={position} icon={icon}>
      <Popup className="custom-popup" maxWidth={320} minWidth={280}>
        <div
          data-testid={`map-popup-${listing.id}`}
          className="cursor-pointer"
          onClick={() => navigate(`/unterkunft/${listing.id}`)}
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          {listing.thumbnailUrl && (
            <div style={{
              width: "100%",
              height: "160px",
              borderRadius: "12px",
              overflow: "hidden",
              marginBottom: "12px"
            }}>
              <img
                src={listing.thumbnailUrl}
                alt={listing.name}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover"
                }}
              />
            </div>
          )}

          <div style={{ padding: "0 4px" }}>
            <h3 style={{
              fontSize: "15px",
              fontWeight: 600,
              color: "#1D1D1F",
              margin: "0 0 6px 0",
              lineHeight: 1.3,
              fontFamily: "'Outfit', sans-serif"
            }}>
              {listing.name}
            </h3>

            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              color: "#86868B",
              fontSize: "12px",
              marginBottom: "8px"
            }}>
              <span>{listing.city}, {listing.country}</span>
            </div>

            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              color: "#86868B",
              fontSize: "12px",
              marginBottom: "10px"
            }}>
              <span>{listing.personCapacity} Gäste</span>
              <span>{listing.bedroomsNumber} Schlafz.</span>
              <span>{listing.bathroomsNumber} Bad</span>
            </div>

            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderTop: "1px solid rgba(0,0,0,0.06)",
              paddingTop: "10px"
            }}>
              <span style={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#1D1D1F",
                fontFamily: "'Outfit', sans-serif"
              }}>
                ab {listing.price} EUR
              </span>
              <span style={{
                fontSize: "11px",
                color: "#86868B"
              }}>/ Nacht</span>
            </div>
          </div>
        </div>
      </Popup>
    </Marker>
  );
}
